import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { api, endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ArrowRight, ChevronLeft, MapPin, User, UserCog } from "lucide-react";
import CatalogSelector from "@/components/CatalogSelector";
import ImageUploader from "@/components/ImageUploader";

const RELATIONSHIPS = [
    {
        id: "self",
        title: "I am this artist",
        blurb: "You'll be the Artist Owner and can invite managers or team members later.",
        Icon: User,
    },
    {
        id: "manager",
        title: "I manage or represent this artist",
        blurb: "You'll get management access. The Artist Owner slot stays open for the artist themselves.",
        Icon: UserCog,
    },
];

export default function ArtistSetup() {
    const nav = useNavigate();
    const { user, setUserAndArtists } = useAuth();
    const [step, setStep] = useState("form"); // "form" | "matches" | "requested"
    const [busy, setBusy] = useState(false);
    const [matches, setMatches] = useState([]);
    const [requestPayload, setRequestPayload] = useState(null);
    const [bootstrapping, setBootstrapping] = useState(true);
    const [bootstrapError, setBootstrapError] = useState(null);
    const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

    const [form, setForm] = useState({
        name: "",
        city: "",
        country: "",
        primary_genre: "",
        additional_genres: [],
        image_url: "",
        relationship: "",
    });

    // Cross-device onboarding resume: server-side state trumps sessionStorage.
    // If the user already has an active artist workspace, route them there.
    // Failures here must NEVER leave the page blank — surface a recoverable
    // error state with clear Try again / Go to account actions.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setBootstrapping(true);
            setBootstrapError(null);
            try {
                const my = await endpoints.authMyArtists();
                if (cancelled) return;
                if (my?.artists && my.artists.length > 0) {
                    const first = my.artists[0];
                    try {
                        localStorage.setItem("pm_active_artist_id", first.id);
                        localStorage.setItem("pm_active_artist_name", first.name || "");
                    } catch {}
                    nav("/workspace", { replace: true });
                    return;
                }
                // Best-effort onboarding-state marker for cross-device resume.
                // If this fails we still let the user fill the form — the
                // POST /onboarding/artist step upserts state on completion.
                try {
                    await endpoints.upsertOnboardingState({ intent: "artist", status: "started" });
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn("[ArtistSetup] onboarding-state upsert failed (non-blocking):", e?.message || e);
                }
                if (!cancelled) setBootstrapping(false);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("[ArtistSetup] bootstrap failed:", err);
                if (!cancelled) {
                    setBootstrapError(formatApiError(err) || "Unknown error");
                    setBootstrapping(false);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bootstrapAttempt]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const canContinue =
        form.name.trim() &&
        form.city.trim() &&
        form.country.trim() &&
        form.primary_genre.trim() &&
        form.relationship;

    // Step 1: submit — first search for duplicates; if any, show match screen.
    const submitStep1 = async (e) => {
        if (e) e.preventDefault();
        if (!canContinue || busy) return;
        setBusy(true);
        try {
            const s = await api.get("/onboarding/artists/search", { params: { q: form.name.trim() } });
            const found = s.data?.matches || [];
            if (found.length > 0) {
                setMatches(found);
                setStep("matches");
            } else {
                await createArtist();
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const createArtist = async () => {
        setBusy(true);
        try {
            const d = await api.post("/onboarding/artist", {
                name: form.name.trim(),
                city: form.city.trim(),
                country: form.country.trim(),
                primary_genre: form.primary_genre.trim(),
                additional_genres: form.additional_genres,
                image_url: form.image_url || null,
                relationship: form.relationship,
            });
            const aid = d.data?.artist?.id;
            const aname = d.data?.artist?.name;
            if (aid) {
                try { localStorage.setItem("pm_active_artist_id", aid); } catch {}
                try { localStorage.setItem("pm_active_artist_name", aname || ""); } catch {}
            }
            await setUserAndArtists(user);
            toast.success(
                d.data?.membership_role === "owner"
                    ? "Artist workspace created — you're the Artist Owner"
                    : "Artist workspace created — you have Manager/Admin access",
            );
            nav("/workspace", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const requestAccess = async (artistId, artistName) => {
        setBusy(true);
        try {
            const rel = form.relationship === "self" ? "self" : "manager";
            const r = await api.post(`/artists/${artistId}/access-requests`, {
                relationship: rel,
            });
            if (r.data?.already_member) {
                toast.success("You already have access — sending you to the workspace");
                try {
                    localStorage.setItem("pm_active_artist_id", artistId);
                    localStorage.setItem("pm_active_artist_name", artistName || "");
                } catch {}
                await setUserAndArtists(user);
                nav("/workspace", { replace: true });
                return;
            }
            setRequestPayload({ artistName, artistId, status: r.data?.request?.status || "pending" });
            setStep("requested");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // ---------- Render ----------
    if (bootstrapping) {
        return (
            <div
                className="max-w-2xl mx-auto px-4 md:px-6 py-16 flex flex-col items-center text-center"
                data-testid="artist-setup-loading"
            >
                <Loader2 className="w-6 h-6 text-[#ff4400] animate-spin" />
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
                <div className="mt-2 text-sm text-[#a3a3a3]">Loading your Artist setup…</div>
            </div>
        );
    }

    if (bootstrapError) {
        return (
            <div className="max-w-lg mx-auto px-4 md:px-6 py-16" data-testid="artist-setup-error">
                <div className="border border-[#2a2a2a] p-8">
                    <div className="flex items-center gap-2 text-[#ff4400]">
                        <AlertTriangle className="w-4 h-4" />
                        <div className="text-[10px] font-mono uppercase tracking-widest">Setup unavailable</div>
                    </div>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-3xl">
                        We couldn't load your Artist setup.
                    </h1>
                    <p className="mt-3 text-sm text-[#a3a3a3]">
                        Something went wrong reaching Project Mint. Your progress is safe — try again, or head back to your account.
                    </p>
                    <div className="mt-6 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => setBootstrapAttempt((n) => n + 1)}
                            className="pm-btn pm-btn-primary"
                            data-testid="artist-setup-error-retry"
                        >
                            Try again
                        </button>
                        <button
                            type="button"
                            onClick={() => nav("/account", { replace: true })}
                            className="pm-btn"
                            data-testid="artist-setup-error-account"
                        >
                            Go to account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === "requested") {
        return (
            <div className="max-w-lg mx-auto px-4 md:px-6 py-16" data-testid="artist-setup-requested">
                <div className="border border-[#2a2a2a] p-8">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Access requested</div>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-3xl">Request sent</h1>
                    <p className="mt-3 text-sm text-[#a3a3a3]">
                        We've sent your access request for <strong className="text-[#f4f4f0]">{requestPayload.artistName}</strong>.
                        {requestPayload.status === "orphaned"
                            ? " There isn't an active Artist Owner on this profile yet — a Project Mint admin will review your request."
                            : " The current Artist Owner will review it in their workspace."}
                    </p>
                    <div className="mt-6 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => nav("/fan", { replace: true })}
                            className="pm-btn pm-btn-primary"
                            data-testid="artist-setup-goto-fan"
                        >
                            Go to my Fan Passport
                        </button>
                        <button
                            type="button"
                            onClick={() => { setStep("form"); setMatches([]); }}
                            className="pm-btn"
                            data-testid="artist-setup-try-again"
                        >
                            Search for a different artist
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === "matches") {
        return (
            <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="artist-setup-matches">
                <button
                    type="button"
                    onClick={() => setStep("form")}
                    className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400] flex items-center gap-1 mb-4"
                    data-testid="artist-setup-matches-back"
                >
                    <ChevronLeft className="w-3 h-3" /> Back
                </button>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Duplicate check</div>
                <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                    Is this your artist profile?
                </h1>
                <p className="mt-2 text-sm text-[#a3a3a3]">
                    We searched Project Mint for artists that already look like "{form.name}". If one is yours, request access instead of creating a duplicate.
                </p>

                <div className="mt-6 space-y-3">
                    {matches.map((m) => (
                        <div
                            key={m.id}
                            className="border border-[#2a2a2a] p-4 flex items-start gap-4"
                            data-testid={`artist-match-${m.id}`}
                        >
                            <img
                                src={m.image_url || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=160"}
                                alt=""
                                className="w-16 h-16 object-cover border border-[#2a2a2a]"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold uppercase text-sm">{m.name}</div>
                                <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {[m.city, m.country].filter(Boolean).join(" · ") || "Location not set"}
                                </div>
                                {m.genre && (
                                    <div className="text-[10px] font-mono uppercase text-[#666] mt-0.5">{m.genre}</div>
                                )}
                                {m.has_owner ? (
                                    <div className="text-[10px] font-mono uppercase text-[#00e676] mt-2">Owner active</div>
                                ) : (
                                    <div className="text-[10px] font-mono uppercase text-[#ffea00] mt-2">Owner slot open</div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => requestAccess(m.id, m.name)}
                                disabled={busy}
                                className="pm-btn text-xs"
                                data-testid={`artist-match-request-${m.id}`}
                            >
                                Request access
                            </button>
                        </div>
                    ))}
                </div>

                <div className="mt-6 border border-[#2a2a2a] p-4 flex items-center justify-between gap-3">
                    <div className="text-sm text-[#a3a3a3]">None of these are mine — my artist is different.</div>
                    <button
                        type="button"
                        onClick={createArtist}
                        disabled={busy}
                        className="pm-btn pm-btn-primary text-xs"
                        data-testid="artist-setup-create-new"
                    >
                        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                        This is a different artist
                    </button>
                </div>
            </div>
        );
    }

    // step === "form"
    return (
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="artist-setup-form">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
            <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-3xl md:text-5xl">
                Set up your artist profile
            </h1>
            <p className="mt-3 text-sm text-[#a3a3a3] max-w-lg">
                Just the essentials — your workspace opens as a private draft. Publish when you're ready.
                You'll still have full Fan Passport access on the same account.
            </p>

            <form onSubmit={submitStep1} className="mt-8 space-y-6">
                <Field label="Artist / stage name" htmlFor="artist-name">
                    <input
                        id="artist-name"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="e.g. Meskerem Sound"
                        className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none text-lg"
                        data-testid="artist-setup-name"
                        required
                    />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="City">
                        <input
                            value={form.city}
                            onChange={(e) => set("city", e.target.value)}
                            placeholder="e.g. Addis Ababa"
                            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none"
                            data-testid="artist-setup-city"
                            required
                        />
                    </Field>
                    <Field label="Country">
                        <input
                            value={form.country}
                            onChange={(e) => set("country", e.target.value)}
                            placeholder="e.g. Ethiopia"
                            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none"
                            data-testid="artist-setup-country"
                            required
                        />
                    </Field>
                </div>

                <Field label="Primary genre">
                    <CatalogSelector
                        mode="single"
                        value={form.primary_genre}
                        onChange={(v) => set("primary_genre", v)}
                        resource="genres"
                        placeholder="Search genres — e.g. Ethio-Jazz"
                        testId="artist-setup-primary-genre"
                    />
                </Field>

                <Field label="Additional genres (optional)">
                    <CatalogSelector
                        mode="multi"
                        value={form.additional_genres}
                        onChange={(v) => set("additional_genres", v)}
                        resource="genres"
                        disabledValues={[form.primary_genre].filter(Boolean)}
                        placeholder="Search or add more genres"
                        testId="artist-setup-additional-genres"
                    />
                </Field>

                <Field label="Profile image (optional)">
                    <ImageUploader
                        value={form.image_url}
                        onChange={(v) => set("image_url", v)}
                        scope="fan"
                        aspect="square"
                        testId="artist-setup-image"
                    />
                </Field>

                <Field label="Relationship to artist">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="artist-setup-relationship">
                        {RELATIONSHIPS.map((r) => {
                            const active = form.relationship === r.id;
                            return (
                                <button
                                    key={r.id}
                                    type="button"
                                    onClick={() => set("relationship", r.id)}
                                    className={`text-left border p-4 transition-colors ${
                                        active ? "border-[#ff4400] bg-[#ff4400]/10" : "border-[#2a2a2a] hover:border-[#666]"
                                    }`}
                                    data-testid={`artist-setup-rel-${r.id}`}
                                    aria-pressed={active}
                                >
                                    <r.Icon className="w-4 h-4 text-[#ff4400]" />
                                    <div className="mt-2 font-display font-bold uppercase text-sm">{r.title}</div>
                                    <div className="mt-1 text-xs text-[#a3a3a3] leading-relaxed">{r.blurb}</div>
                                </button>
                            );
                        })}
                    </div>
                </Field>

                <div className="pt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => nav("/fan", { replace: true })}
                        className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]"
                        data-testid="artist-setup-skip"
                    >
                        Skip for now — go to Fan Passport
                    </button>
                    <button
                        type="submit"
                        disabled={!canContinue || busy}
                        className="pm-btn pm-btn-primary"
                        data-testid="artist-setup-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children, htmlFor }) {
    return (
        <div>
            <label htmlFor={htmlFor} className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1 block">
                {label}
            </label>
            {children}
        </div>
    );
}
